import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import AlertTriangleIcon from 'ext-ui/svg/icon/AlertTriangleIcon'
import moment from 'moment'
import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import ManageSubscriptionHeader from 'src/app/ManageSubscription/components/ManageSubscriptionHeader/ManageSubscriptionHeader'
import ManageSubscriptionPromo from 'src/app/ManageSubscription/components/ManageSubscriptionPromo/ManageSubscriptionPromo'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import HorizontalContentCollection from 'src/components/HorizontalContentCollection/HorizontalContentCollection'
import LayoutShell from 'src/components/LayoutShell/LayoutShell'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import ManageOrgSubscriptionBox from 'src/components/ManageOrgSubscriptionBox/ManageOrgSubscription'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import { SubnavDesign } from 'src/components/Subnav/Subnav'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { SubscriptionStatus } from 'src/models/organizations.models'
import { UserFeature } from 'src/models/user.models'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import {
  useQueryOrganizationSaasTier,
  useQueryOrganizationSubscription,
} from 'src/queries/organizations/saas.queries'
import { getOrganization } from 'src/redux/actions/data/organizations.actions'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectOrganizationIdFromRoute,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import {
  selectAppName,
  selectIsExtendBrand,
} from 'src/redux/selectors/session.selectors'

const ManageSubscription: React.FC = () => {
  const user = useSelector(selectUserListModelFromState)
  const organizationId = useSelector(selectOrganizationIdFromRoute)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const appName = useSelector(selectAppName)
  const isExtendBrand = useSelector(selectIsExtendBrand)

  const translate = useTranslate()

  const { data: organizationSubscription } = useQueryOrganizationSubscription({
    orgId: organization?.id,
    enabled: !!organization?.id,
  })

  const dispatch = useBackgroundDispatch()
  useEffect(() => {
    if (organizationId) {
      dispatch(getOrganization(organizationId, true))
    }
  }, [organizationId])

  const {
    data: currentPlan,
    isLoading: isLoadingCurrentPlan,
    isSuccess,
  } = useQueryOrganizationSaasTier({
    orgId: organization?.id || '',
    enabled: !!organization?.id,
  })

  const { show_new_org_settings: showNewOrgSettings } = useSelector(
    selectFeatureFlaggerFromState,
  )

  const { data: userFeatures, isLoading: isFeaturesLoading } =
    useQueryUserFeatures([UserFeature.SAAS_ENABLED])

  let cancelsAt
  switch (organizationSubscription?.subscription?.state.status) {
    case SubscriptionStatus.NON_RENEWING:
      cancelsAt = organizationSubscription?.subscription?.state.cancelsAt
      break
    default:
      break
  }

  // Show loading state while data is being fetched
  if (
    user === undefined ||
    organization === undefined ||
    currentPlan === undefined ||
    isFeaturesLoading
  ) {
    return <LoadingBackgroundPage />
  }

  // Show not-found page if SaaS is not enabled for this user
  if (!userFeatures?.SAAS_ENABLED || !user.hasOrgAdminPermissions) {
    return <NotFoundBackgroundPage />
  }

  // Original UI (when feature flag is off)
  if (!showNewOrgSettings) {
    return (
      <>
        <Helmet>
          <title>{`Organization Info | ${appName}`}</title>
          <meta
            name="description"
            content={`Organization Info page of ${appName}`}
          />
        </Helmet>
        <LayoutShell
          subnavOptions={[]}
          subnavDesign={SubnavDesign.Floating}
          content={
            <>
              {cancelsAt && (
                <AlertPanel variant="warning" icon={<AlertTriangleIcon />}>
                  {translate('Your Pro subscription ends ${date}.', {
                    date: moment(cancelsAt).format('MMMM D, YYYY'),
                  })}
                </AlertPanel>
              )}
              {isExtendBrand && isSuccess && (
                <HorizontalContentCollection splitCenter>
                  <ManageOrgSubscriptionBox
                    user={user}
                    organization={organization}
                    currentPlan={currentPlan}
                    isLoadingCurrentPlan={isLoadingCurrentPlan}
                    isSuccess={isSuccess}
                  />
                  <div></div>
                </HorizontalContentCollection>
              )}
            </>
          }
          title={<span>{translate('Manage Plan')}</span>}
          buttons={[]}
        />
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>{`Organization Info | ${appName}`}</title>
        <meta
          name="description"
          content={`Organization Info page of ${appName}`}
        />
      </Helmet>
      <LayoutPage padding={'medium'}>
        <div className={'flex-column gap-12'}>
          <ManageSubscriptionHeader
            currentTier={currentPlan.tier}
            organizationId={organizationId}
          />
          <ManageSubscriptionPromo
            organizationId={organizationId}
            currentTier={currentPlan.tier}
          />
        </div>
      </LayoutPage>
    </>
  )
}

export default ManageSubscription
